/**
 * Parse Collect&Go price text → cents. Browser-safe (no DB).
 *
 * Cogo often splits euros/cents across nodes (`0,` + `89`), so `innerText`
 * becomes `0, 89 /st`. Prefer sell-unit price (/st|/kg|/pak) over volume
 * straplines like `(€3,56 / l)` or `(€0,4 / st)` — those are subunit
 * comparable prices (see {@link cogoVolumePriceFromText}).
 */

import { isPromoPieceThreshold, parsePackSize } from './cogoUnits'

function normalizeCogoPriceText(text: string): string {
  return String(text)
    .replace(/\s+/g, ' ')
    .replace(/(\d)[,.]\s+(\d{1,2})\b/g, '$1,$2')
    .trim()
}

/** "0,4" → 40 cents; "0,40" / "3,56" → 40 / 356. */
function eurosFracToCents(euros: number, frac: string): number | null {
  if (!Number.isFinite(euros) || !/^\d{1,2}$/.test(frac)) return null
  const centsPart = Number(frac.padEnd(2, '0').slice(0, 2))
  if (!Number.isFinite(centsPart)) return null
  return euros * 100 + centsPart
}

export type CogoVolumePrice = {
  cents: number
  unit: 'st' | 'kg' | 'l' | 'pak'
}

/**
 * Parenthetical volume / subunit price: `(€0,4 / st)`, `(€3,56 / l)`.
 * For multi-piece artikelen sold /st (eieren), `/st` here is per piece —
 * not the shelf carton price.
 */
export function cogoVolumePriceFromText(
  text: string | null | undefined
): CogoVolumePrice | null {
  if (!text) return null
  const normalized = normalizeCogoPriceText(text)
  if (!normalized) return null
  const m = normalized.match(
    /\(\s*€\s*(\d+)[,.](\d{1,2})\s*\/\s*(st|kg|l|pak)\s*\)/i
  )
  if (!m) return null
  const cents = eurosFracToCents(Number(m[1]), m[2]!)
  if (cents == null || !(cents > 0)) return null
  return { cents, unit: m[3]!.toLowerCase() as CogoVolumePrice['unit'] }
}

/**
 * When shelf and volume are both /st, volume is per subunit (egg etc.).
 * Infer piece count from shelf ÷ volume (rounded).
 */
export function packSizeFromShelfAndVolume(
  shelfCents: number | null | undefined,
  volume: CogoVolumePrice | null | undefined
): number | null {
  if (
    shelfCents == null ||
    !(shelfCents > 0) ||
    !volume ||
    volume.unit !== 'st' ||
    !(volume.cents > 0)
  ) {
    return null
  }
  const n = Math.round(shelfCents / volume.cents)
  return n > 1 ? n : null
}

/**
 * Resolve pieces-per-sell-unit: title "6st" → unitLabel "2st" → scrape (when
 * title/label agrees) → shelf÷volume `/st`. Never trust promo "vanaf 3 st".
 *
 * Productview often has WCSPackSize=2 while the display name is just
 * "Zwarte pens" — unitLabel (`2st`) must still count.
 */
export function resolvePackSize(input: {
  packSize?: number | null
  display?: string | null
  unitLabel?: string | null
  priceCents?: number | null
  priceText?: string | null
}): number | null {
  const display = input.display || ''
  const unitLabel = input.unitLabel || ''
  const fromTitle = parsePackSize(display).packSize
  if (fromTitle != null && fromTitle > 1) return fromTitle

  const fromLabel = parsePackSize(unitLabel).packSize
  if (fromLabel != null && fromLabel > 1) return fromLabel

  const scraped =
    typeof input.packSize === 'number' && input.packSize > 1
      ? input.packSize
      : null
  // Keep scraped count when title/label mentions the same Nst (not a leftover
  // from promo straplines scraped into pack_size with a bare product name).
  if (scraped != null && !isPromoPieceThreshold(display)) {
    const mentioned = `${display} ${unitLabel}`.match(
      /(\d+)\s*(?:stuks?|st)\b/i
    )
    if (mentioned && Number(mentioned[1]) === scraped) return scraped
  }

  return packSizeFromShelfAndVolume(
    input.priceCents,
    cogoVolumePriceFromText(input.priceText)
  )
}

export function cogoPriceCentsFromText(
  text: string | null | undefined
): number | null {
  if (!text) return null
  const normalized = normalizeCogoPriceText(text)
  if (!normalized) return null

  // Search tiles: accessible label is authoritative.
  const labeled = normalized.match(/Prijs:\s*€\s*(\d+)[,.](\d{2})/i)
  if (labeled) {
    return Number(labeled[1]) * 100 + Number(labeled[2])
  }

  // Prefer shelf / sell-unit price (/st|/kg|/pak or "per stuk").
  // Skip parenthetical volume straplines — handled separately.
  const withoutVolume = normalized.replace(
    /\(\s*€\s*\d+[,.]\d{1,2}\s*\/\s*(?:st|kg|l|pak)\s*\)/gi,
    ' '
  )
  const unit = withoutVolume.match(
    /(\d+)[,.](\d{2})\s*(?:€\s*)?\/\s*(st|kg|pak)\b|€\s*(\d+)[,.](\d{2})\s*(?:per\s+)?(?:stuk|st|kg|pak)\b/i
  )
  if (unit) {
    const euros = Number(unit[1] ?? unit[4])
    const cents = Number(unit[2] ?? unit[5])
    if (Number.isFinite(euros) && Number.isFinite(cents)) {
      return euros * 100 + cents
    }
  }

  // "€ 0,59 per stuk059/st…" — no word boundary after stuk when glued.
  const glued = withoutVolume.match(
    /€\s*(\d+)[,.](\d{2})\s*per\s*stuk(?=\d|\/|\s|$)/i
  )
  if (glued) {
    return Number(glued[1]) * 100 + Number(glued[2])
  }

  const euro = withoutVolume.match(
    /€\s*(\d+)[,.](\d{2})\b|(\d+)[,.](\d{2})\s*€/
  )
  if (euro) {
    const euros = Number(euro[1] ?? euro[3])
    const cents = Number(euro[2] ?? euro[4])
    if (Number.isFinite(euros) && Number.isFinite(cents)) {
      return euros * 100 + cents
    }
  }

  const bare = withoutVolume.match(/(\d+)[,.](\d{2})\b/)
  if (!bare) return null
  return Number(bare[1]) * 100 + Number(bare[2])
}

/** Format cents as €x,xx (Belgian/Dutch). */
export function formatCogoPriceCents(
  cents: number | null | undefined
): string | null {
  if (cents == null || !(cents > 0) || !Number.isFinite(cents)) return null
  const euros = Math.floor(cents / 100)
  const rest = Math.abs(cents % 100)
  return `€${euros},${String(rest).padStart(2, '0')}`
}

export type CogoChoicePriceInput = {
  priceCents?: number | null
  /** Shelf price unit from productview / scrape (`kg` = basePrice is €/kg). */
  backendUnit?: string | null
  weightConversionFactor?: number | null
  /** Pieces per sell unit (e.g. 12 eieren) — not a multipack pak. */
  packSize?: number | null
  unitTypes?: Array<'P' | 'W' | 'PA'> | null
  unitLabel?: string | null
  display?: string | null
}

/** True when pack size is volume (l/cl/ml), not weight. */
function isLiquidPack(
  unitLabel?: string | null,
  display?: string | null
): boolean {
  const t = `${unitLabel || ''} ${display || ''}`.toLowerCase()
  return /\d+(?:[.,]\d+)?\s*(?:l|cl|ml)\b/.test(t)
}

/**
 * Prefer comparable unit price (€/kg, €/l, or €/st) over shelf total.
 * - backendUnit=kg → priceCents is already €/kg (weight articles / pears)
 * - Dual/loose with W in unitTypes + weightConversionFactor → still €/kg shelf
 *   (WCF is average piece weight, not a bag price divisor)
 * - Packed shelf /st with weightConversionFactor → derive from priceCents × 1000 / weightConversionFactor
 * - Multi-piece /st (eieren 6st) → priceCents / packSize as €/st
 * - Pure W unit type → priceCents is already €/kg
 * - Otherwise → plain shelf/stuk price
 */
export function formatCogoChoicePrice(
  input: CogoChoicePriceInput
): string | null {
  const cents = input.priceCents
  if (cents == null || !(cents > 0) || !Number.isFinite(cents)) return null

  const modes = input.unitTypes || []
  const pricedPerKg =
    input.backendUnit === 'kg' ||
    (modes.includes('W') &&
      !modes.includes('PA') &&
      (modes.includes('P') || modes.length === 1))

  if (pricedPerKg) {
    const formatted = formatCogoPriceCents(cents)
    return formatted ? `${formatted}/kg` : null
  }

  const weightConversionFactor = input.weightConversionFactor
  if (weightConversionFactor != null && weightConversionFactor > 0) {
    const unitCents = Math.round((cents * 1000) / weightConversionFactor)
    const formatted = formatCogoPriceCents(unitCents)
    if (!formatted) return null
    const suffix = isLiquidPack(input.unitLabel, input.display) ? '/l' : '/kg'
    return `${formatted}${suffix}`
  }

  const packSize = input.packSize
  if (packSize != null && packSize > 1) {
    const unitCents = Math.round(cents / packSize)
    const formatted = formatCogoPriceCents(unitCents)
    return formatted ? `${formatted}/st` : null
  }

  return formatCogoPriceCents(cents)
}

/**
 * Line total from shelf price.
 * - backendUnit=kg + quantity in kg → price × kg
 * - backendUnit=kg + quantity in st → price × (units × weightConversionFactor / 1000)
 * - otherwise → price × units (shelf € per sell unit)
 */
export function cogoLineTotalCents(opts: {
  priceCents?: number | null
  backendUnit?: string | null
  units: number
  quantityUnit: 'st' | 'kg'
  weightConversionFactor?: number | null
}): number | null {
  const cents = opts.priceCents
  const units = opts.units
  if (
    cents == null ||
    !(cents > 0) ||
    !Number.isFinite(cents) ||
    !(units > 0)
  ) {
    return null
  }
  if (opts.backendUnit === 'kg') {
    if (opts.quantityUnit === 'kg') {
      return Math.round(cents * units)
    }
    if (
      opts.weightConversionFactor != null &&
      opts.weightConversionFactor > 0
    ) {
      return Math.round((cents * units * opts.weightConversionFactor) / 1000)
    }
    return null
  }
  return Math.round(cents * units)
}

/** Comparable €/kg (or €/st when no weight) for sorting / display. */
export function cogoUnitPriceCents(opts: {
  priceCents?: number | null
  backendUnit?: string | null
  weightConversionFactor?: number | null
  packSize?: number | null
  unitTypes?: Array<'P' | 'W' | 'PA'> | null
}): number | null {
  const cents = opts.priceCents
  if (cents == null || !(cents > 0) || !Number.isFinite(cents)) return null
  const modes = opts.unitTypes || []
  const pricedPerKg =
    opts.backendUnit === 'kg' ||
    (modes.includes('W') &&
      !modes.includes('PA') &&
      (modes.includes('P') || modes.length === 1))
  if (pricedPerKg) return cents
  // Packed /st shelf price → derive €/kg (or keep cents for liquids via caller).
  if (opts.weightConversionFactor != null && opts.weightConversionFactor > 0) {
    return Math.round((cents * 1000) / opts.weightConversionFactor)
  }
  if (opts.packSize != null && opts.packSize > 1) {
    return Math.round(cents / opts.packSize)
  }
  return cents
}

/** Cost of one sell unit (one pack / piece) for combo planning. */
export function cogoSellUnitCostCents(opts: {
  priceCents?: number | null
  backendUnit?: string | null
  weightConversionFactor?: number | null
}): number | null {
  const cents = opts.priceCents
  if (cents == null || !(cents > 0) || !Number.isFinite(cents)) return null
  if (opts.backendUnit === 'kg') {
    if (
      opts.weightConversionFactor != null &&
      opts.weightConversionFactor > 0
    ) {
      return Math.round((cents * opts.weightConversionFactor) / 1000)
    }
    return null
  }
  return cents
}
